import React, { useEffect, useState } from "react";
import { ClipLoader } from "react-spinners";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ProgressBar from "../../../components/Form/ProgressBar";
import CustomButton from "../../../components/Form/ArrowButton";
import AditionalServiceCard from "../../../components/Form/AditionalServiceCard";
import OrderSummarySidebar from "../../../components/Dashboard/OrderSummarySidebar";
import useFirebaseFileUpload from "../../../hooks/useFirebaseFileUpload";
import { useThunk } from "../../../hooks/useThunk";
import {
  checkWriterExists,
  placeOrder,
  setFormDataGlobState,
  setPendingPaymentOrderId,
} from "../../../store";
import {
  ADITIONAL_SERVICE_LABELS,
  ADITIONAL_SERVICES,
} from "../../../constants/commonConstants";
import { errorMessages } from "../../../constants/frontendErrorMessages";
import { calculateCost } from "../../../utils/costCalculator";
import { AditionalServiceCostCalculator } from "../../../utils/aditionalServiceCostCalculator";
import { normalizeAdditionalServices } from "../../../utils/additionalServices";
import { currencyFormatter } from "../../../utils/formatting";
import { getCustomerDiscountName } from "../../../utils/seasonalDiscount";
import showToast from "../../../utils/toastNotifications";

const getValidatedOrderFormData = (data) => {
  const fieldNames = [
    "subject",
    "topic",
    "orderInstructions",
    "referenceStyle",
    "writingType",
    "typeOfPaper",
    "academicLevel",
    "typeOfEnglish",
    "deadline",
    "spacing",
    "previousOrderId",
    "aditionalServices",
    "requestedWriterId",
    "numberOfFigures",
    "pages",
    "slides",
    "needDocumentation",
    "needPresentation",
    "budgetIncrement",
  ];

  return fieldNames.reduce((formData, fieldName) => {
    if (Object.prototype.hasOwnProperty.call(data || {}, fieldName)) {
      formData[fieldName] = data[fieldName];
    }

    return formData;
  }, {});
};

const getFilesComparableData = (files) => {
  return (files || []).map((file) => ({
    name: file?.name || file,
    size: file?.size || null,
    lastModified: file?.lastModified || null,
  }));
};

const validateNumberOfFigures = (value) => {
  if (!value) return "Please Provide the number of Figures/Charts";

  const numericValue = Number(value);
  if (!Number.isInteger(numericValue) || numericValue < 1 || numericValue > 50) {
    return "Figures/Charts must be between 1 and 50";
  }

  return "";
};

const normalizeNumberOfFigures = (value) => {
  if (value === "") return "";

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";

  return Math.min(Math.max(Math.floor(numericValue), 1), 50).toString();
};

const AdditionalServices = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const createOrder = useSelector((state) => state.createOrder);
  const [doPlaceOrder, isPlacingOrder] = useThunk(placeOrder);
  const [doFetchWriterbyId, isLoadingWriter] = useThunk(checkWriterExists);
  const [
    doUploadRefrencesToFirebase,
    isUploadRefrencesToFirebaseLoading,
  ] = useFirebaseFileUpload();

  const [formData, setFormData] = useState({
    ...getValidatedOrderFormData(createOrder.formData),
    aditionalServices: normalizeAdditionalServices(createOrder.formData?.aditionalServices),
    requestedWriterId: createOrder.formData?.requestedWriterId || "",
    numberOfFigures: createOrder.formData?.numberOfFigures || "",
  });
  const [errors, setErrors] = useState({});

  const steps = [
    { fullLabel: "Calculation", shortLabel: "Calculation" },
    { fullLabel: "Paper Instructions", shortLabel: "Instructions" },
    { fullLabel: "Additional Services", shortLabel: "Services" },
    { fullLabel: "Secure Payment", shortLabel: "Payment" },
  ];

  useEffect(() => {
    if (
      !(createOrder?.formData?.typeOfPaper) ||
      !(createOrder?.formData?.academicLevel) ||
      !(createOrder?.formData?.deadline) ||
      !(createOrder?.formData?.spacing) ||
      !(createOrder?.formData?.writingType) ||
      !(createOrder?.formData?.typeOfEnglish)
    ) {
      navigate("/calculation");
      return;
    }

    if (
      !(createOrder?.formData?.subject) ||
      !(createOrder?.formData?.orderInstructions) ||
      !(createOrder?.formData?.referenceStyle)
    ) {
      navigate("/paper-instructions");
    }
  }, []);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      ...getValidatedOrderFormData(createOrder.formData),
      aditionalServices: normalizeAdditionalServices(createOrder.formData?.aditionalServices),
      requestedWriterId: createOrder.formData?.requestedWriterId || "",
      numberOfFigures: createOrder.formData?.numberOfFigures || "",
    }));
  }, [createOrder.formData]);

  const totalPages = Number(formData.pages || 0) + Number(formData.slides || 0);
  const referralExtraPercent = createOrder?.referralDiscount?.eligible
    ? Number(createOrder.referralDiscount.referralPercent || 0)
    : 0;
  const loyaltyExtraPercent = !createOrder?.referralDiscount?.eligible && createOrder?.loyaltyProgress?.enabled
    ? Math.max(0, Number(createOrder.loyaltyProgress?.currentLevel?.discountPercent || 30) - 30)
    : 0;
  const baseDiscountName = getCustomerDiscountName(createOrder?.loyaltyProgress);
  const livePriceDetails = calculateCost({
    academicLevel: formData.academicLevel,
    deadline: formData.deadline,
    spacing: formData.spacing,
    pages: totalPages,
    typeOfPaper: formData.typeOfPaper,
    budgetIncrement: Number(formData.budgetIncrement || 0),
    writingType: formData.writingType,
    typeOfEnglish: formData.typeOfEnglish,
    aditionalServices: formData.aditionalServices,
    returnDetails: true,
    additionalDiscountPercent: referralExtraPercent || loyaltyExtraPercent,
    additionalDiscountName: referralExtraPercent ? "New customer referral discount" : baseDiscountName,
    additionalDiscountType: referralExtraPercent ? "referral" : "loyalty",
    baseDiscountName,
  });
  const liveOrderTotal = typeof livePriceDetails === "object"
    ? Number(livePriceDetails.discountedTotalCost || 0)
    : 0;

  const changeWriterBudget = (amount) => {
    setFormData((prev) => ({
      ...prev,
      budgetIncrement: Math.max(0, Number(prev.budgetIncrement || 0) + amount),
    }));
  };

  const getDaysUntilDeadline = (deadline) => {
    if (!deadline) return 0;

    const lower = deadline.toLowerCase().trim();
    if (lower.includes("hour")) return parseFloat(lower) / 24;
    if (lower.includes("day")) return parseFloat(lower);

    return 0;
  };

  const isAditionalServiceSelected = (label) => {
    return (formData.aditionalServices || []).some((service) => service.label === label);
  };

  const handleAditionalServices = (service) => {
    if (!service || service.isIncluded) return;

    setFormData((prev) => {
      const exists = (prev.aditionalServices || []).find((s) => s.label === service.label);
      if (exists) {
        const updated = { ...prev };

        if (service.inputName) {
          updated[service.inputName] = "";
        }

        setErrors((prevErrors) => ({
          ...prevErrors,
          requestedWriterId: "",
          numberOfFigures: "",
        }));

        return {
          ...updated,
          aditionalServices: (prev.aditionalServices || []).filter((s) => s.label !== service.label),
        };
      }

      return {
        ...prev,
        aditionalServices: [
          ...(prev.aditionalServices || []),
          { label: service.label, costType: service.costType },
        ],
      };
    });
  };

  const handleServiceInputChange = (name, value) => {
    if (name === "numberOfFigures") {
      const normalizedValue = normalizeNumberOfFigures(value);
      setFormData((prev) => ({
        ...prev,
        numberOfFigures: normalizedValue,
      }));
      setErrors((prev) => ({
        ...prev,
        numberOfFigures: normalizedValue ? "" : validateNumberOfFigures(normalizedValue),
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleServiceInputBlur = (name, value) => {
    if (name === "numberOfFigures") {
      const normalizedValue = normalizeNumberOfFigures(value);
      setFormData((prev) => ({
        ...prev,
        numberOfFigures: normalizedValue,
      }));
      setErrors((prev) => ({
        ...prev,
        numberOfFigures: validateNumberOfFigures(normalizedValue),
      }));
    }
  };

  useEffect(() => {
    const updatedServices = (formData.aditionalServices || []).map((service) => {
      if (service.label === ADITIONAL_SERVICE_LABELS.FIGURES) {
        return {
          ...service,
          perPageMultiplier: formData.numberOfFigures || 0,
        };
      }

      return service;
    });

    setFormData((prev) => ({
      ...prev,
      aditionalServices: updatedServices,
    }));
  }, [formData.numberOfFigures]);

  const calculateIndividualServiceCost = (service) => {
    const { orderCost, serviceBaseOrderCost, costPerPage } = calculateCost({
      academicLevel: formData.academicLevel,
      deadline: formData.deadline,
      spacing: formData.spacing,
      pages: totalPages,
      typeOfPaper: formData.typeOfPaper,
      budgetIncrement: formData.budgetIncrement,
      writingType: formData.writingType,
      typeOfEnglish: formData.typeOfEnglish,
      aditionalServices: formData.aditionalServices,
      returnDetails: true,
    });

    const pricedService = service.label === ADITIONAL_SERVICE_LABELS.FIGURES
      ? { ...service, perPageMultiplier: formData.numberOfFigures || 0 }
      : service;
    const cost = Number(
      AditionalServiceCostCalculator(
        pricedService,
        serviceBaseOrderCost ?? orderCost,
        costPerPage,
        totalPages
      )
    ).toFixed(2) || "00.00";

    return currencyFormatter.format(cost);
  };

  const hasDraftChangedSincePendingOrder = (nextFormData) => {
    return JSON.stringify(getValidatedOrderFormData(nextFormData)) !==
      JSON.stringify(getValidatedOrderFormData(createOrder.formData));
  };

  const handleBack = () => {
    dispatch(setFormDataGlobState(getValidatedOrderFormData(formData)));
    navigate("/paper-instructions");
  };

  const validateServices = async () => {
    const nextErrors = {};
    const figureSelected = (formData.aditionalServices || []).find(
      (service) => service.label === ADITIONAL_SERVICE_LABELS.FIGURES
    );
    const writerSelected = (formData.aditionalServices || []).find(
      (service) => service.label === ADITIONAL_SERVICE_LABELS.SPECIFIC_WRITER
    );

    if (figureSelected) {
      const figureError = validateNumberOfFigures(formData.numberOfFigures);
      if (figureError) nextErrors.numberOfFigures = figureError;
    }

    if (writerSelected) {
      if (!formData.requestedWriterId) {
        nextErrors.requestedWriterId = "Please Provide Writer ID";
      } else if (!formData.requestedWriterId.startsWith("ID") || formData.requestedWriterId.length < 6) {
        nextErrors.requestedWriterId = errorMessages.INVALID_WRITER_ID;
      } else {
        const writer = await doFetchWriterbyId({ requestedWriterId: formData.requestedWriterId });
        if (writer?.error) {
          nextErrors.requestedWriterId = writer.error.message;
        } else {
          setFormData((prev) => ({
            ...prev,
            requestedWriterId: writer.response.data?.requestedWriterId,
          }));
        }
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = async () => {
    const isValid = await validateServices();
    if (!isValid) {
      showToast("error", "Please check the additional service details");
      return;
    }

    const orderFormData = {
      ...getValidatedOrderFormData(formData),
      aditionalServices: normalizeAdditionalServices(formData.aditionalServices),
    };
    dispatch(setFormDataGlobState(orderFormData));

    if (createOrder.pendingPaymentOrderId && !hasDraftChangedSincePendingOrder(orderFormData)) {
      navigate(`/secure-payment/${createOrder.pendingPaymentOrderId}`);
      return;
    }

    const fileUploadResult = await doUploadRefrencesToFirebase(
      createOrder.formDataFiles || [],
      "order"
    );

    if (!fileUploadResult.success) {
      console.error("Upload Error:", fileUploadResult.error);
      showToast("error", fileUploadResult?.error?.message || "Failed to upload files");
      return;
    }

    const orderData = {
      ...orderFormData,
      topic: formData.topic,
      referenceDocuments: fileUploadResult.uploadedUrls,
    };

    const result = await doPlaceOrder(orderData);
    if (result.success) {
      const pendingPaymentOrderId = result?.response?.data?._id || "";
      dispatch(setPendingPaymentOrderId(pendingPaymentOrderId));
      navigate(`/secure-payment/${pendingPaymentOrderId}`);
    } else {
      showToast("error", result?.error?.message);
    }
  };

  return (
    <div className="flex flex-col flex-grow h-screen min-w-[340px] overflow-x-hidden">
      <div className="text-primary font-roboto font-semibold text-[24px] pb-4 ml-[24px] xl:mt-[24px] mt-[104px]">
        <h2 className="mr-2">Step Three (Additional Services)</h2>
      </div>

      <div className="overflow-y-auto xl:mt-10">
        <div className="bg-white mt-[32px] ml-6 xl:ml-[48px] mr-[24px] lg:mr-[48px] mb-[48px] border border-medium-gray rounded-lg py-8 px-[16px] lg:px-[48px] xl:px-[136px] md:py-10 max-w-full md:max-w-[2200px] w-auto mx-auto">
          <ProgressBar steps={steps} activeStep={3} completedStep={2} />

          <div className="grid w-full grid-cols-1 items-start gap-6 1600-br:grid-cols-[minmax(0,1fr)_280px]">
            <div className="flex flex-col justify-center items-center mt-[-40px] w-full">
              <div className="mt-20 w-full">
                <div className="flex flex-start w-full font-medium text-[16px] text-[#2F3464]">
                  Additional Services (Optional)
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8 w-full">
                  {ADITIONAL_SERVICES.map((service) => (
                    <AditionalServiceCard
                      key={service.label}
                      onSelect={() => handleAditionalServices(service)}
                      price={service.cost}
                      title={service.label}
                      description={service.note}
                      icon={service.icon}
                      isAnabledInput={!!service.isAnabledInput}
                      isSelected={isAditionalServiceSelected(service.label)}
                      inputName={service.inputName}
                      inputValue={formData?.[service.inputName] ?? ""}
                      inputPlaceholder={service?.inputPlaceholder ?? ""}
                      inputError={errors?.[service.inputName] ?? ""}
                      onInputChange={handleServiceInputChange}
                      onInputBlur={handleServiceInputBlur}
                      inputType={service.inputType}
                      calculatedPrice={isAditionalServiceSelected(service.label) && calculateIndividualServiceCost(service)}
                      isDesabled={
                        typeof service.isAvailable === "function"
                          ? !service.isAvailable({
                              daysUntilDeadline: getDaysUntilDeadline(formData.deadline),
                            })
                          : !service.isAvailable
                      }
                      inputProps={service?.inputProps}
                      tooltip={service.tooltip}
                      badge={service.badge}
                      isIncluded={service.isIncluded}
                    />
                  ))}
                </div>

                <section className="mt-8 rounded-[16px] border border-medium-gray bg-white p-4 shadow-sm sm:p-5" aria-labelledby="writer-budget-heading">
                  <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="max-w-xl">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 id="writer-budget-heading" className="font-inter text-[15px] font-semibold text-secondary">
                          Increase Writer Budget
                        </h3>
                        <span className="rounded-full bg-soft-blue px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-blue-shades">
                          Optional
                        </span>
                      </div>
                      <p className="mt-1.5 font-inter text-[12px] leading-5 text-gray sm:text-[13px]">
                        Optionally add to the budget available for your writer. This does not change your deadline or order requirements.
                      </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-3 self-start sm:self-center">
                      <button
                        type="button"
                        aria-label="Decrease additional writer budget by $5"
                        disabled={Number(formData.budgetIncrement || 0) === 0}
                        onClick={() => changeWriterBudget(-5)}
                        className="flex h-11 w-11 items-center justify-center rounded-[12px] border border-medium-gray bg-white text-[24px] font-medium text-blue-shades transition hover:bg-soft-blue disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <span aria-hidden="true" className="leading-none">−</span>
                      </button>
                      <div className="min-w-[108px] rounded-[12px] border border-medium-blue bg-soft-blue px-3 py-2 text-center">
                        <span className="block text-[9px] font-semibold uppercase tracking-[0.12em] text-gray">
                          Added
                        </span>
                        <span className="block text-[21px] font-semibold leading-7 text-blue-shades">
                          {currencyFormatter.format(Number(formData.budgetIncrement || 0))}
                        </span>
                      </div>
                      <button
                        type="button"
                        aria-label="Increase additional writer budget by $5"
                        onClick={() => changeWriterBudget(5)}
                        className="flex h-11 w-11 items-center justify-center rounded-[12px] border border-medium-gray bg-white text-[24px] font-medium text-blue-shades transition hover:bg-soft-blue"
                      >
                        <span aria-hidden="true" className="leading-none">+</span>
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col gap-2 border-t border-medium-gray pt-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <span className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-gray">New order total</span>
                      <strong className="mt-0.5 block font-roboto text-[22px] leading-7 text-blue-shades">
                        {currencyFormatter.format(liveOrderTotal)}
                      </strong>
                      <span className="mt-0.5 block text-[11px] leading-4 text-gray">
                        {Number(formData.budgetIncrement || 0) > 0
                          ? `Includes ${currencyFormatter.format(Number(formData.budgetIncrement))} additional writer budget.`
                          : "No additional writer budget added."}
                      </span>
                    </div>
                    {Number(formData.budgetIncrement || 0) > 0 && (
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, budgetIncrement: 0 }))}
                        className="self-start text-[11px] font-semibold text-blue-shades underline underline-offset-2 sm:self-auto"
                      >
                        Remove additional budget
                      </button>
                    )}
                  </div>
                </section>

                <div className="flex justify-between gap-1.5 mt-8 w-full">
                  <CustomButton
                    text="Back"
                    iconDirection="left"
                    iconPosition="before"
                    onClick={handleBack}
                    width="clamp(76px, 24vw, 104px)"
                    height="44px"
                    buttonClassName="gap-2 px-2 text-[12px] sm:px-3"
                    iconClassName="h-6 w-6 shrink-0"
                  />
                  <CustomButton
                    text={
                      isUploadRefrencesToFirebaseLoading || isPlacingOrder || isLoadingWriter
                        ? (
                          <ClipLoader
                            loading={true}
                            speedMultiplier={0.5}
                            size={22}
                            color="#60a5fa"
                          />
                        )
                        : (
                          <>
                            <span className="sm:hidden">Next: Payment</span>
                            <span className="hidden sm:inline">Next: Secure Payment</span>
                          </>
                        )
                    }
                    iconDirection="right"
                    iconPosition="after"
                    onClick={handleNext}
                    width="clamp(128px, 46vw, 190px)"
                    height="44px"
                    buttonClassName="gap-1 px-1.5 text-[11px] leading-none hover:bg-black disabled:hover:bg-slate-300 sm:gap-2 sm:px-3 sm:text-[12px]"
                    iconClassName="h-3 w-3 sm:h-4 sm:w-4"
                    disabled={isUploadRefrencesToFirebaseLoading || isPlacingOrder || isLoadingWriter}
                  />
                </div>
              </div>
            </div>

            <OrderSummarySidebar
              formData={formData}
              pages={Number(formData.pages || 0)}
              slides={Number(formData.slides || 0)}
              totalUnits={totalPages}
              subject={formData.subject}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdditionalServices;
